import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { userStylesAtom } from "@/store/style/style-state"
import { useAtomValue, useSetAtom } from "jotai"
import StylesList, { StylesListType } from "./StylesList"
import { currentDisplayedStylesAtom } from "@/store/style/polarr-styles"
import { StyleResponseDTO } from "@/api/dto/styles"
import { Button } from "../ui/Button"
import { Spinner } from "../ui/Spinner"
import { getCompanyName } from "@/utils/product-name"

export const UserStylesPanel = (props: {
	onSelect: (style: StyleResponseDTO, skipConfirm?: boolean) => void
	selectedStyle: StyleResponseDTO | undefined
	onAddNew?: () => void
	isFullWidth?: boolean
	stylesListType: StylesListType
	onViewClicked?: (style: StyleResponseDTO) => void
}) => {
	const {
		data: styles,
		isInitialLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useAtomValue(userStylesAtom)
	const setCurrentDisplayedStyles = useSetAtom(currentDisplayedStylesAtom)
	const loadMoreRef = useRef<HTMLDivElement>(null)
	const { t } = useTranslation("components")

	const sortedStyles = useMemo(() => {
		return [...styles].sort((a, b) => {
			// Convert to Date if it's a string/number, otherwise use as-is
			const aTime =
				a.createdAt instanceof Date
					? a.createdAt.getTime()
					: new Date(a.createdAt).getTime()
			const bTime =
				b.createdAt instanceof Date
					? b.createdAt.getTime()
					: new Date(b.createdAt).getTime()
			return bTime - aTime
		})
	}, [styles])

	useEffect(() => {
		setCurrentDisplayedStyles(sortedStyles)
	}, [sortedStyles, setCurrentDisplayedStyles])

	useEffect(() => {
		const loadMore = loadMoreRef.current
		if (!loadMore || !hasNextPage || isInitialLoading) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting && !isFetchingNextPage) {
					void fetchNextPage()
				}
			},
			{
				rootMargin: "0px 0px 300px 0px",
				threshold: 0,
			},
		)

		observer.observe(loadMore)

		return () => {
			observer.disconnect()
		}
	}, [fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading])

	const renderContent = () => {
		if (isInitialLoading) {
			return (
				<div className="flex size-full animate-pulse items-center justify-center">
					<Spinner size={24} />
				</div>
			)
		}
		if (sortedStyles.length > 0) {
			return (
				<>
					<StylesList
						type={props.stylesListType}
						styles={sortedStyles}
						onSelect={(style) => props.onSelect(style)}
						selectedStyle={props.selectedStyle}
						onAddNew={props.onAddNew}
						isFullWidth={props.isFullWidth}
						onViewClicked={props.onViewClicked}
					/>
					{hasNextPage && <div ref={loadMoreRef} className="h-1 w-full" />}
					{isFetchingNextPage && (
						<div className="flex w-full items-center justify-center py-4">
							<Spinner size={16} />
						</div>
					)}
				</>
			)
		} else {
			return (
				<div className="flex size-full flex-col items-center justify-center space-y-2 text-center">
					<div className="text-md font-bold text-base-300">
						{t("userStylesPanel.createYourOwn")}
					</div>
					<div className="pb-2 text-sm text-base-400">
						{t("userStylesPanel.willLearnYourStyleAnd", { var0: getCompanyName() })}
					</div>
					<Button
						trackingLabel="create_style"
						aria-label={t("userStylesPanel.ariaCreatePersonalAIStyle")}
						variant={"primary"}
						onClick={() => {
							props.onAddNew?.()
						}}
					>
						{t("userStylesPanel.createPersonal")}
					</Button>
				</div>
			)
		}
	}

	return (
		<div className="flex w-full flex-1 flex-col">
			<div className="flex-1">{renderContent()}</div>
		</div>
	)
}
